import { useState } from "react";
import { Paperclip, Plus } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { useChat } from "@/contexts/ChatContext";
import { TRABAJO_ESTADO, formatFechaCorta, fechaVencida, ordenarTrabajos } from "@/lib/clienteNombre";
import { cn } from "@/lib/utils";

function TrabajoChip({ trabajo, onClick }) {
  const { usuariosByEmail } = useChat();
  const est = TRABAJO_ESTADO[trabajo.estado] || TRABAJO_ESTADO.por_empezar;
  const vencida = trabajo.estado !== "hecho" && fechaVencida(trabajo.fecha_limite);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 flex items-center gap-2 h-9 rounded-full border pl-2.5 pr-1.5 text-xs bg-background hover:shadow-sm transition max-w-[280px]",
        trabajo.estado === "hecho" && "opacity-60"
      )}
      title={`${trabajo.titulo} · ${est.label}`}
    >
      <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", est.dot)} />
      <span className={cn("truncate font-medium", trabajo.estado === "hecho" && "line-through")}>{trabajo.titulo}</span>
      {trabajo.adjuntos?.length > 0 && <Paperclip className="w-3 h-3 shrink-0 text-muted-foreground" />}
      {trabajo.fecha_limite && (
        <span className={cn("shrink-0 tabular-nums", vencida ? "text-destructive font-medium" : "text-muted-foreground")}>{formatFechaCorta(trabajo.fecha_limite)}</span>
      )}
      {trabajo.asignados?.length > 0 ? (
        <span className="flex -space-x-1.5 shrink-0">
          {trabajo.asignados.slice(0, 3).map((e) => (
            <span key={e} className="rounded-full ring-2 ring-background inline-flex">
              <UserAvatar user={usuariosByEmail[e] || { email: e }} size={22} />
            </span>
          ))}
        </span>
      ) : (
        <span className="w-1" />
      )}
    </button>
  );
}

/** Barra de trabajos anclada bajo la cabecera del chat. Los «Hecho» se ocultan salvo que se pidan. */
export function TrabajosBar({ trabajos, onNuevo, onAbrir }) {
  const [verHechos, setVerHechos] = useState(false);
  const hechos = trabajos.filter((t) => t.estado === "hecho");
  const visibles = ordenarTrabajos(
    trabajos.filter((t) => verHechos || t.estado !== "hecho"),
    { porEstado: true }
  );

  return (
    <div className="shrink-0 border-b bg-background/80 backdrop-blur">
      <div className="flex items-center gap-2 px-3 md:px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          onClick={onNuevo}
          className="shrink-0 flex items-center gap-1.5 h-9 rounded-full border border-dashed px-3 text-xs text-muted-foreground hover:text-foreground hover:border-foreground/40"
        >
          <Plus className="w-3.5 h-3.5" />
          {trabajos.length === 0 ? "Añadir trabajo pendiente" : "Trabajo"}
        </button>
        {visibles.map((t) => (
          <TrabajoChip key={t.id} trabajo={t} onClick={() => onAbrir(t.id)} />
        ))}
        {hechos.length > 0 && (
          <button type="button" onClick={() => setVerHechos((v) => !v)} className="shrink-0 h-9 px-2 text-xs text-muted-foreground hover:text-foreground whitespace-nowrap">
            {verHechos ? "Ocultar hechos" : `${hechos.length} hecho${hechos.length === 1 ? "" : "s"}`}
          </button>
        )}
      </div>
    </div>
  );
}
